import { useMemo } from 'react';
import { Ellipse as KEllipse, Group, Image as KImage, Line, Rect as KRect, Text } from 'react-konva';
import useImage from 'use-image';
import type { Image as ImageShape, Shape, Sticky } from '@frame/shared';
import { shapeBBox } from './binding/shapesMap';
import { useTokens } from '../styles/theme';
import type { CanvasTokens } from '../styles/tokens';
import { effectiveTextStyle, konvaFontStyle, type ResolvedTextStyle } from './textStyle';
import { fitFontSize } from './textFit';

// Sticky inner padding (world units). Mirrored by TextEditor's sticky
// geometry so the textarea covers the same area the Konva text occupies.
export const STICKY_INNER_PAD_X = 12;
export const STICKY_INNER_PAD_TOP = 12;
export const STICKY_INNER_PAD_BOTTOM = 10;

interface Props {
  shape: Shape;
  selected?: boolean;
  // Peer-side selections: each entry is one remote user that has this shape
  // selected. Rendered as concentric rings outside the local selection ring,
  // colored with the peer's cursor color (Day 5).
  peerSelections?: { color: string }[];
  // When true, the shape's Konva <Text> child is skipped so the DOM
  // <textarea> overlay owns the text. The background/border still render
  // so the shape doesn't visually vanish while you edit it.
  editing?: boolean;
}

// Renders one shape, plus a selection ring when selected. Every shape's
// outermost Konva node gets `id={shape.id}` and `name="shape"` so the Canvas
// hit-test can walk up from e.target to identify what was clicked.
export function ShapeRenderer({ shape, selected = false, peerSelections, editing = false }: Props) {
  const tokens = useTokens();
  return (
    <Group id={shape.id} name="shape" x={shape.x} y={shape.y} rotation={shape.rotation ?? 0}>
      {renderShape(shape, tokens, editing)}
      {selected && <SelectionRing shape={shape} color={tokens.selection} offset={2} />}
      {peerSelections?.map((p, i) => (
        <SelectionRing
          key={i}
          shape={shape}
          color={p.color}
          // Stack rings outward: peer 0 sits 4px out, peer 1 at 7px, etc.
          // Local ring (if also selected) lives at 2px so peers visibly
          // ride outside it.
          offset={selected ? 4 + i * 3 : 2 + i * 3}
        />
      ))}
      {shape.locked && <LockBadge shape={shape} color={tokens.fgPrimary} />}
    </Group>
  );
}

// Each case renders relative to (0, 0) — the parent Group handles positioning.
// When `editing` is true the per-shape Konva <Text> is omitted so the DOM
// <textarea> overlay can own the text without double-rendering.
function renderShape(shape: Shape, t: CanvasTokens, editing: boolean) {
  switch (shape.type) {
    case 'sticky':
      return <StickyShape shape={shape} t={t} editing={editing} />;
    case 'rect': {
      const style = effectiveTextStyle(shape, t);
      return (
        <>
          <KRect
            width={shape.w}
            height={shape.h}
            fill={shape.fill}
            stroke={shape.stroke}
            strokeWidth={1.5}
            cornerRadius={4}
          />
          {!editing && shape.text && (
            <Text
              text={shape.text}
              width={shape.w}
              height={shape.h}
              align={style.align}
              verticalAlign={style.verticalAlign}
              fontSize={style.fontSize}
              fontStyle={konvaFontStyle(style)}
              textDecoration={style.underline ? 'underline' : ''}
              fontFamily="Inter, sans-serif"
              fill={style.color}
              listening={false}
            />
          )}
        </>
      );
    }
    case 'ellipse': {
      const style = effectiveTextStyle(shape, t);
      return (
        <>
          <KEllipse
            x={shape.w / 2}
            y={shape.h / 2}
            radiusX={shape.w / 2}
            radiusY={shape.h / 2}
            fill={shape.fill}
            stroke={shape.stroke}
            strokeWidth={1.5}
          />
          {!editing && shape.text && (
            <Text
              text={shape.text}
              width={shape.w}
              height={shape.h}
              align={style.align}
              verticalAlign={style.verticalAlign}
              fontSize={style.fontSize}
              fontStyle={konvaFontStyle(style)}
              textDecoration={style.underline ? 'underline' : ''}
              fontFamily="Inter, sans-serif"
              fill={style.color}
              listening={false}
            />
          )}
        </>
      );
    }
    case 'text': {
      if (editing) return null;
      const style = effectiveTextStyle(shape, t);
      return (
        <Text
          text={shape.text}
          fontSize={style.fontSize}
          fontStyle={konvaFontStyle(style)}
          textDecoration={style.underline ? 'underline' : ''}
          align={style.align}
          fontFamily="Inter, sans-serif"
          fill={style.color}
        />
      );
    }
    case 'frame': {
      const style = effectiveTextStyle(shape, t);
      return (
        <>
          <KRect
            width={shape.w}
            height={shape.h}
            fill="transparent"
            stroke={shape.color ?? t.borderStrong}
            strokeWidth={1}
          />
          {!editing && (
            <Text
              text={shape.label}
              x={0}
              y={-18}
              fontSize={style.fontSize}
              fontStyle={konvaFontStyle(style)}
              textDecoration={style.underline ? 'underline' : ''}
              align={style.align}
              fontFamily="Inter, sans-serif"
              fill={style.color}
              listening={false}
            />
          )}
        </>
      );
    }
    case 'arrow': {
      const from = shape.from.kind === 'point' ? shape.from : null;
      const to = shape.to.kind === 'point' ? shape.to : null;
      if (!from || !to) return null;
      return (
        <Line
          points={[from.x, from.y, to.x, to.y]}
          stroke={t.fgPrimary}
          strokeWidth={1.5}
          lineCap="round"
        />
      );
    }
    case 'freehand':
      return (
        <Line
          points={shape.points}
          stroke={shape.stroke}
          strokeWidth={shape.strokeWidth}
          lineCap="round"
          lineJoin="round"
          tension={0.4}
        />
      );
    case 'image':
      return <ImageShapeNode shape={shape} t={t} />;
    default:
      return null;
  }
}

// Compute the largest font size at which the sticky's text fits in its
// inner box. Returns the user's chosen size when it already fits — the auto-
// shrink only kicks in when content would otherwise be clipped.
export function stickyInnerSize(shape: Sticky): { w: number; h: number } {
  return {
    w: Math.max(0, shape.w - STICKY_INNER_PAD_X * 2),
    h: Math.max(0, shape.h - STICKY_INNER_PAD_TOP - STICKY_INNER_PAD_BOTTOM),
  };
}

export function stickyFitFontSize(shape: Sticky, style: ResolvedTextStyle): number {
  const inner = stickyInnerSize(shape);
  return fitFontSize(shape.text ?? '', {
    innerWidth: inner.w,
    innerHeight: inner.h,
    baseFontSize: style.fontSize,
    fontFamily: 'Inter, sans-serif',
    bold: style.bold,
    italic: style.italic,
  });
}

function StickyShape({ shape, t, editing }: { shape: Sticky; t: CanvasTokens; editing: boolean }) {
  const style = effectiveTextStyle(shape, t);
  // Auto-shrink the font when text wouldn't fit — text is never clipped.
  // Recomputes when content or box dimensions change; cheap (binary search
  // over canvas measureText). Skipped while editing because the DOM
  // textarea owns rendering and runs its own fit pass.
  const fittedSize = useMemo(
    () => (editing ? style.fontSize : stickyFitFontSize(shape, style)),
    [editing, shape, style],
  );
  const inner = stickyInnerSize(shape);
  return (
    <>
      <KRect
        width={shape.w}
        height={shape.h}
        fill={t.sticky[shape.color]}
        shadowColor={t.stickyShadow.color}
        shadowOpacity={t.stickyShadow.opacity}
        shadowBlur={t.stickyShadow.blur}
        shadowOffsetY={t.stickyShadow.offsetY}
        cornerRadius={4}
      />
      {!editing && (
        <Text
          text={shape.text}
          width={inner.w}
          height={inner.h}
          x={STICKY_INNER_PAD_X}
          y={STICKY_INNER_PAD_TOP}
          fontSize={fittedSize}
          fontStyle={konvaFontStyle(style)}
          textDecoration={style.underline ? 'underline' : ''}
          align={style.align}
          verticalAlign={style.verticalAlign}
          lineHeight={1.4}
          fontFamily="Inter, sans-serif"
          fill={style.color}
          wrap="word"
          listening={false}
        />
      )}
    </>
  );
}

// Image renders as a Konva Image once the asset has loaded; while loading
// (or if the data URL fails to parse) we show a subtle placeholder rect so
// the shape's footprint stays selectable + lasso-hittable.
function ImageShapeNode({ shape, t }: { shape: ImageShape; t: CanvasTokens }) {
  const [img, status] = useImage(shape.src);
  if (status === 'loaded' && img) {
    return <KImage image={img} width={shape.w} height={shape.h} alt={shape.alt} />;
  }
  return (
    <KRect
      width={shape.w}
      height={shape.h}
      fill={t.borderDefault}
      stroke={t.borderStrong}
      strokeWidth={1}
      cornerRadius={2}
      dash={[4, 4]}
    />
  );
}

// Tiny padlock glyph anchored to the top-left of a locked shape. Drawn from
// primitives (no font/icon dep) so it scales with the canvas via the parent
// Group's transform. The badge is non-listening; selection/drag still hit
// the shape body underneath.
function LockBadge({ shape, color }: { shape: Shape; color: string }) {
  const bbox = shapeBBox(shape);
  if (!bbox) return null;
  // Badge dimensions in world units. Stays the same on screen because the
  // parent Group inherits the viewport transform.
  const bodyW = 10;
  const bodyH = 7;
  const shackleH = 4;
  const shackleW = 6;
  const pad = 4;
  // Anchor to the inside of the shape's top-left corner.
  const x = pad;
  const y = pad;
  return (
    <Group x={x} y={y} listening={false}>
      <KRect
        x={(bodyW - shackleW) / 2}
        y={0}
        width={shackleW}
        height={shackleH + 2}
        stroke={color}
        strokeWidth={1.2}
        cornerRadius={shackleW / 2}
      />
      <KRect
        x={0}
        y={shackleH}
        width={bodyW}
        height={bodyH}
        fill={color}
        cornerRadius={1}
      />
    </Group>
  );
}

// Selection ring — spec §6.6: 1.5px solid, 2px offset (further for stacked
// peer rings). Color is theme-token for local; cursor-palette for peers.
function SelectionRing({
  shape,
  color,
  offset,
}: {
  shape: Shape;
  color: string;
  offset: number;
}) {
  const bbox = shapeBBox(shape);
  if (!bbox) return null;
  return (
    <KRect
      x={-offset}
      y={-offset}
      width={bbox.w + offset * 2}
      height={bbox.h + offset * 2}
      stroke={color}
      strokeWidth={1.5}
      cornerRadius={4}
      listening={false}
    />
  );
}
